/**
 * Searchable supplier dropdown for the Call Off Management list (customer
 * only). Typing narrows the option list; picking an option filters the list
 * to that supplier.
 *
 * Split out of CallOffManagementTab (CR-7717) to keep that component under the
 * 500-line limit. The selected supplier stays owned by the list; only the
 * dropdown's own open/typed state lives here.
 *
 * @link CallOffManagementTab for the consumer.
 */
import { useState, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { SupplierOption } from './callOffListConfig';

interface CallOffSupplierFilterProps {
  suppliers: SupplierOption[];
  /** Selected supplier id as a string ('' = all suppliers). */
  supplierFilter: string;
  setSupplierFilter: (id: string) => void;
  /** Called when a selection change should send the list back to page 1. */
  onPageReset: () => void;
}

export default function CallOffSupplierFilter({
  suppliers, supplierFilter, setSupplierFilter, onPageReset,
}: CallOffSupplierFilterProps) {
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);

  const filteredSupplierOptions = useMemo(() => {
    const term = supplierSearchTerm.toLowerCase();
    if (!term) return suppliers;
    return suppliers.filter(s => s.name.toLowerCase().includes(term));
  }, [suppliers, supplierSearchTerm]);

  const selectedSupplierName = useMemo(() => {
    if (!supplierFilter) return '';
    const supplier = suppliers.find(s => String(s.id) === supplierFilter);
    return supplier ? `${supplier.name}${supplier.supplier_code ? ` (${supplier.supplier_code})` : ''}` : '';
  }, [supplierFilter, suppliers]);

  return (
    <div className="relative w-52">
      <div className="relative">
        <input
          type="text"
          placeholder="Search suppliers..."
          value={supplierFilter ? selectedSupplierName : supplierSearchTerm}
          onChange={(e) => {
            setSupplierSearchTerm(e.target.value);
            setSupplierFilter('');
            setSupplierDropdownOpen(true);
          }}
          onFocus={() => setSupplierDropdownOpen(true)}
          className="w-full px-3 py-2 pr-8 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
        {supplierFilter && (
          <button
            type="button"
            onClick={() => {
              setSupplierFilter('');
              setSupplierSearchTerm('');
              onPageReset();
            }}
            className="absolute right-8 top-2 p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {supplierDropdownOpen && filteredSupplierOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
          {filteredSupplierOptions.slice(0, 50).map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                setSupplierFilter(String(s.id));
                setSupplierSearchTerm('');
                setSupplierDropdownOpen(false);
                onPageReset();
              }}
              className="w-full px-3 py-2 text-left hover:bg-muted text-sm"
            >
              {s.name}{s.supplier_code ? ` (${s.supplier_code})` : ''}
            </button>
          ))}
        </div>
      )}
      {supplierDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setSupplierDropdownOpen(false)}
        />
      )}
    </div>
  );
}
